import { callLinkAction } from '@helpers/fetchCustom';
import getResponseJson from '@helpers/getResponseJson';
import { notification } from '@utils/utils';
import { fetchPositionRecruitmentAutomationsEditor, RECRUITMENT_AUTOMATIONS_EDITOR } from '@components/PositionAutomation/automationsApi';
import { categorizeAutomationRules } from '@components/PositionAutomation/helper';
import { buildRecruitmentAutomationPostPayload } from '@components/PositionAutomation/buildRecruitmentAutomationPayload';
import { validateRecruitmentAutomationForm } from '@components/PositionAutomation/automationSubmitValidation';
import { deserializeRecruitmentAutomationRule } from '@components/PositionAutomation/deserializeRecruitmentAutomationRule';
import {
    createEmptyAutomationRuleForm,
    emptyAutomationRulesByOutcome,
    emptyAutomationRulesByStep,
} from './PositionAutomation.state';
import { selectResolvedSelectedAction, selectSaveTarget } from './PositionAutomation.selectors';

const categorizeAutomationRulesByStepAndOutcome = (state) => {
    const rules = state.automationEditor.data?.automation_rules;
    if (!rules?.length) {
        state.rulesByStep = emptyAutomationRulesByStep;
        state.rulesByOutcome = emptyAutomationRulesByOutcome;
        return;
    }
    const { rulesByStep, rulesByOutcome } = categorizeAutomationRules(rules);
    state.rulesByStep = rulesByStep;
    state.rulesByOutcome = rulesByOutcome;
};

export const createActions = (set, get, jobId) => ({
    fetchEditor: async (signal) => {
        if (!jobId) {
            return;
        }
        set((state) => {
            state.automationEditor.loading = true;
            state.automationEditor.error = null;
            state.automationConfigs.data = null;
            state.automationConfigs.error = null;
            state.automationConfigs.loading = false;
        });
        try {
            const response = await fetchPositionRecruitmentAutomationsEditor(jobId, { signal });
            if (signal?.aborted) {
                return;
            }
            const body = await getResponseJson(response, { notification: false });
            if (signal?.aborted) {
                return;
            }
            if (!response.ok) {
                set((state) => {
                    state.automationEditor.data = null;
                    state.automationEditor.error = body?.notification || body?.message || 'Request failed';
                });
                return;
            }
            const data = body[RECRUITMENT_AUTOMATIONS_EDITOR] ?? null;
            set((state) => {
                state.automationEditor.data = data;
                state.automationEditor.error = null;
            });
        } catch (error) {
            if (signal?.aborted || error?.name === 'AbortError') {
                return;
            }
            set((state) => {
                state.automationEditor.error = error.message || 'Request failed';
                state.automationEditor.data = null;
            });
        } finally {
            set((state) => {
                state.automationEditor.loading = false;
                if (!signal?.aborted) {
                    state.automationEditor.loaded = true;
                }
                categorizeAutomationRulesByStepAndOutcome(state);
            });
        }
    },

    fetchConfigs: async (options = {}) => {
        const { notification = true, signal, mode } = options;
        const editor = get().automationEditor;
        if (!editor.loaded) {
            return false;
        }
        const links = editor.data?._links;
        if (!links?.get_configs) {
            return false;
        }
        let query = options.query ? { ...options.query } : {};
        if (mode === 'outcome') {
            query = { ...query, outcome: true };
        } else {
            query = { ...query, step: true };
        }
        set((state) => {
            state.automationConfigs.loading = true;
            state.automationConfigs.error = null;
        });
        try {
            const response = await callLinkAction(links, 'get_configs', { query, signal });
            if (signal?.aborted) {
                return false;
            }
            if (!response.ok) {
                const errBody = await getResponseJson(response, { notification });
                if (signal?.aborted) {
                    return false;
                }
                set((state) => {
                    state.automationConfigs.error = errBody?.notification || errBody?.message || 'Request failed';
                });
                return false;
            }
            const json = await getResponseJson(response, { notification });
            if (signal?.aborted) {
                return false;
            }
            set((state) => {
                state.automationConfigs.error = null;
                state.automationConfigs.data = json;
            });
            return json;
        } catch (error) {
            if (signal?.aborted || error?.name === 'AbortError') {
                return false;
            }
            set((state) => {
                state.automationConfigs.error = error.message || 'Request failed';
            });
            return false;
        } finally {
            set((state) => {
                state.automationConfigs.loading = false;
            });
        }
    },

    setAutomationModal: (automationModal) =>
        set((state) => {
            state.ui.automationModal = automationModal;
        }),

    setRuleDetail: (ruleDetail) =>
        set((state) => {
            state.ui.ruleDetail = ruleDetail;
            state.ui.ruleDetailLoaded = true;
            state.ui.ruleDetailError = null;
        }),

    patchAutomationRuleForm: (patch) =>
        set((state) => {
            state.ui.automationRuleForm = { ...state.ui.automationRuleForm, ...patch };
        }),
    
    patchActionValue: (patch) =>
        set((state) => {
            const actionValue = { ...(state.ui.automationRuleForm.actionValue ?? {}) };
            Object.entries(patch).forEach(([key, value]) => {
                if (value === null || value === undefined) {
                    delete actionValue[key];
                } else {
                    actionValue[key] = value;
                }
            });
            state.ui.automationRuleForm.actionValue = actionValue;
        }),

    resetAutomationRuleForm: () =>
        set((state) => {
            state.ui.automationRuleForm = createEmptyAutomationRuleForm();
            state.ui.automationRuleHydratedId = null;
            state.ui.automationRuleSaving = false;
            state.actionResources.data = null;
            state.actionResources.loading = false;
            state.actionResources.error = null;
            state.actionResources.actionId = null;
        }),

    fetchActionResources: async (actionId, options = {}) => {
        const { signal, notification: showNotification = false, query = {} } = options;
        const editor = get().automationEditor;
        const links = editor.data?._links;
        if (!actionId || !links?.get_action_resources) {
            return null;
        }
        set((state) => {
            state.actionResources.loading = true;
            state.actionResources.error = null;
            state.actionResources.actionId = actionId;
        });
        try {
            const response = await callLinkAction(links, 'get_action_resources', {
                query: { action_id: actionId, ...query },
                signal,
            });
            if (signal?.aborted) {
                return null;
            }
            if (!response.ok) {
                if (signal?.aborted) {
                    return null;
                }
                if (response.status === 404) {
                    set((state) => {
                        state.actionResources.error = null;
                        state.actionResources.data = null;
                    });
                    return null;
                }
                const errBody = await getResponseJson(response, { notification: showNotification });
                if (signal?.aborted) {
                    return null;
                }
                set((state) => {
                    state.actionResources.error =
                        errBody?.notification || errBody?.message || 'Request failed';
                    state.actionResources.data = null;
                });
                return null;
            }
            const json = await getResponseJson(response, { notification: showNotification });
            if (signal?.aborted) {
                return null;
            }
            set((state) => {
                state.actionResources.error = null;
                state.actionResources.data = json;
            });
            return json;
        } catch (error) {
            if (signal?.aborted || error?.name === 'AbortError') {
                return null;
            }
            set((state) => {
                state.actionResources.error = error.message || 'Request failed';
                state.actionResources.data = null;
            });
            return null;
        } finally {
            set((state) => {
                state.actionResources.loading = false;
            });
        }
    },

    syncAutomationRuleFormFromRuleDetail: () =>
        set((state) => {
            const modal = state.ui.automationModal;
            if (!modal) {
                state.ui.automationRuleHydratedId = null;
                return;
            }
            const ruleDetail = modal.ruleDetail;
            if (!ruleDetail) {
                state.ui.automationRuleHydratedId = null;
                return;
            }
            const configs = state.automationConfigs.data;
            if (!configs || state.automationConfigs.loading) {
                return;
            }
            const rid = ruleDetail.id;
            if (state.ui.automationRuleHydratedId === rid) {
                return;
            }
            state.ui.automationRuleHydratedId = rid;
            const next = deserializeRecruitmentAutomationRule(ruleDetail, configs);
            state.ui.automationRuleForm = {
                ...createEmptyAutomationRuleForm(),
                ...next,
                actionValue: next.actionValue && typeof next.actionValue === 'object' ? { ...next.actionValue } : {},
            };
        }),

    saveAutomationRule: async () => {
        const s0 = get();
        const modal = s0.ui.automationModal;
        if (!modal) {
            return { ok: false };
        }
        const readOnly = Boolean(modal.readOnly);
        const category = modal.category;
        const contextId = modal.contextId;
        const ruleDetail = modal.ruleDetail;
        const links = s0.automationEditor.data?._links;
        const draft = s0.ui.automationRuleForm;
        const { event, cron, groups, topOperator, actionValue } = draft;
        const resolvedSelectedAction = selectResolvedSelectedAction(s0);
        const isEdit = Boolean(ruleDetail);
        const saveLink = isEdit ? ruleDetail?._links?.put : links?.post;
        if (readOnly || !saveLink?.url) {
            return { ok: false };
        }
        const validationKey = validateRecruitmentAutomationForm({
            category,
            contextId,
            event,
            cron,
            groups,
            selectedAction: resolvedSelectedAction,
            actionValue,
        });
        if (validationKey) {
            return { ok: false, validationKey };
        }
        const { category: saveCategory, contextId: saveContextId } = selectSaveTarget(s0);
        const payload = buildRecruitmentAutomationPostPayload({
            category: saveCategory,
            contextId: saveContextId,
            event,
            cron,
            groups,
            topOperator,
            actionId: resolvedSelectedAction.id,
            actionValue,
        });
        set((state) => {
            state.ui.automationRuleSaving = true;
        });
        try {
            const response = isEdit
                ? await callLinkAction(ruleDetail._links, 'put', { body: JSON.stringify(payload) })
                : await callLinkAction(links, 'post', { body: JSON.stringify(payload) });
            await getResponseJson(response, { notification: true });
            if (response.ok) {
                await get().refetchEditor();
                get().closeAutomationModal();
                return { ok: true };
            }
            return { ok: false };
        } catch (err) {
            return { ok: false, errorMessage: err?.message || 'Request failed' };
        } finally {
            set((state) => {
                state.ui.automationRuleSaving = false;
            });
        }
    },

    closeAutomationModal: () => {
        void get().fetchConfigs({ notification: false });
        set((state) => {
            state.ui.automationModal = null;
            state.ui.automationRuleForm = createEmptyAutomationRuleForm();
            state.ui.automationRuleHydratedId = null;
            state.ui.automationRuleSaving = false;
            state.actionResources.data = null;
            state.actionResources.loading = false;
            state.actionResources.error = null;
            state.actionResources.actionId = null;
        });
    },

    refetchEditor: () => {
        const controller = new AbortController();
        return get().fetchEditor(controller.signal);
    },

    deleteAutomationRule: async (rule) => {
        const response = await callLinkAction(rule._links, 'delete');
        await getResponseJson(response, { notification: true });
        if (response.ok) {
            await get().refetchEditor();
            void get().fetchConfigs({ notification: false });
        }
    },

    openAutomationRule: async (rule, { category, contextId }) => {
        if (!rule._links?.get_rule) {
            return;
        }
        get().setFetchingRuleId(rule.id);
        try {
            const response = await callLinkAction(rule._links, 'get_rule');
            const body = await getResponseJson(response, { notification: true });
            if (response.ok && body) {
                get().setAutomationModal({
                    category,
                    contextId,
                    ruleDetail: body,
                    readOnly: !body._links?.put,
                });
            }
        } catch (err) {
            notification(err?.message || 'Request failed', { type: 'error' });
        } finally {
            get().setFetchingRuleId(null);
        }
    },

    setFetchingRuleId: (fetchingRuleId) =>
        set((state) => {
            state.ui.fetchingRuleId = fetchingRuleId;
        }),
});
